import React from 'react';
import {StyleSheet, TouchableHighlight, View, ViewStyle} from 'react-native';
import Animated, {Easing, FadeIn, FadeOut, Layout} from 'react-native-reanimated';

import {AirtableFeedItem, AirtablePromotionItem} from '../../models/airtable';
// Specific module, not the `queries/promotions` barrel: this is a leaf tile that
// needs one pure predicate, and the barrel would pull the query hook and
// `queryClient` (whose module load starts TanStack persistence) into its graph.
import {isDrawnWinnersPromotion} from '../../queries/promotions/predicates';
import theme from '../../style/theme';
import {getFeedDiscoverTileImage} from '../../utils/feedAndDiscover/media';
import {getHeroImageDetail} from '../../utils/heroImage';
import {getFirstTwoLines} from '../../utils/string';
import Markdown from '../ui-controls/Markdown';
import Text from '../ui-controls/Text';

import {AirtableContentItem} from './models';

type BaseTileProps = {
  item: AirtableContentItem;
  customStyles: Record<string, ViewStyle>;
  pressCallback: (feedItem: AirtableContentItem) => void;
  paused: boolean;
  visibleCallback?: (isVisible: boolean) => void;
  includeByline: boolean;
  isWebDesktopScreen?: boolean;
  headlineVariant?: 'heading1' | 'heading2' | 'heading3' | 'heading4';
  /**
   * Disable entering/exiting/layout animations.
   * Required for FlashList/recycling lists where animations fire on every cell recycle.
   */
  disableAnimations?: boolean;
  /** Skip InViewPort inside a virtualized list (FlashList) to prevent iOS Fabric crashes. */
  isVirtualized?: boolean;
  /** Virtualized lists only: true for the single most-visible cell (single-active video player). */
  isActive?: boolean;
  /** Absolutely positioned node stacked on the tile image only, never over the text holder. */
  overlay?: React.ReactNode;
};

const BaseTile = ({
  item,
  customStyles,
  pressCallback = () => {},
  paused = true,
  visibleCallback = () => {},
  includeByline = false,
  isWebDesktopScreen = false,
  headlineVariant = 'heading3',
  disableAnimations = false,
  isVirtualized = false,
  isActive = false,
  overlay,
}: BaseTileProps) => {
  // // Used only in tiles on the Feed screen. Discover has too many
  // // instances of videos playing that wrapping them in InViewPort
  // // shits the UI thread on iOS. Easiest to just let them keep on
  // // playing and not bother pausing them when they're out of view.
  // const [paused, setPaused] = useState(true);
  // useFocusEffect(
  //   React.useCallback(() => {
  //     // Do something when the screen is focused
  //     // component.current.startAutoplay(false);
  //     setPaused(false);
  //
  //     return () => {
  //       // Do something when the screen is unfocused
  //       // Useful for cleanup functions
  //       // component.current.stopAutoplay();
  //       setPaused(true);
  //     };
  //   }, []),
  // );

  const itemAsPromotion = item as AirtablePromotionItem;
  const itemAsFeedItem = item as AirtableFeedItem;

  const isDrawnWinners = isDrawnWinnersPromotion(itemAsPromotion);
  const imageDetail = getHeroImageDetail(item, isWebDesktopScreen);

  const tileContent = (
    <TouchableHighlight
      onPress={() => pressCallback(item)}
      underlayColor={theme.colour.general.secondary}
      style={[styles.touchable, customStyles.touchable]}
      activeOpacity={0.7}
      delayPressIn={150}>
      <View style={[styles.container, customStyles.container]} testID="base-tile-container">
        <View style={styles.imageStack} testID="base-tile-image-stack">
          {getFeedDiscoverTileImage(imageDetail, paused, visibleCallback, isVirtualized, isActive)}
          {overlay}
        </View>
        <View style={[styles.textHolder, customStyles.textHolder]} testID="base-tile-text-holder">
          <Text variant={headlineVariant} style={customStyles.headline} testID="base-tile-headline">
            {itemAsFeedItem.tileHeadline || itemAsPromotion.bannerHeadline || item.headline}
          </Text>
          {isDrawnWinners && itemAsPromotion.drawDate ? (
            <Text
              variant="caption"
              testID="base-tile-description"
              style={styles.drawnWinnersCaptionCopy}
              adjustsFontSizeToFit={false}>
              Draw date: {itemAsPromotion.drawDate}
            </Text>
          ) : (
            (itemAsFeedItem.firstCopy || itemAsPromotion.description) &&
            !!includeByline && (
              <Markdown variant="caption">
                {getFirstTwoLines(itemAsFeedItem.firstCopy || itemAsPromotion.description)}
              </Markdown>
            )
          )}
        </View>
      </View>
    </TouchableHighlight>
  );

  // Skip animations for recycling lists (FlashList) - animations fire on every cell recycle
  if (disableAnimations) {
    return <View>{tileContent}</View>;
  }

  return (
    <Animated.View
      entering={FadeIn.duration(500)}
      exiting={FadeOut.duration(250)}
      layout={Layout.easing(Easing.out(Easing.exp))}>
      {tileContent}
    </Animated.View>
  );
};

export default BaseTile;

const styles = StyleSheet.create({
  container: {
    backgroundColor: 'white',
    borderRadius: 4,
    borderWidth: 0.5,
    borderColor: '#CCC',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 5,
    },
    shadowOpacity: 0.29,
    shadowRadius: 4.65,
    elevation: 7,
    marginHorizontal: 0,
  },
  // Anchors `overlay` to the image only, so a badge can never cover the headline.
  imageStack: {
    position: 'relative',
  },
  touchable: {
    borderRadius: 4,
    marginBottom: 8,
  },
  textHolder: {
    width: '100%',
    backgroundColor: 'white',
    borderBottomLeftRadius: 4,
    borderBottomRightRadius: 4,
    paddingHorizontal: 0,
    paddingVertical: 0,
  },
  headline: {
    color: theme.colour.text.heading.primary,
    fontWeight: 'bold',
  },
  drawnWinnersCaptionCopy: {
    paddingTop: 2,
  },
});
